/**
 * SEO最適化のためのヘルパー関数
 */

/**
 * 地域ベースの検索ページ用タイトルを生成
 * @param {string} location - 地域名
 * @param {string} type - 検索タイプ ('room'/'renter')
 * @returns {string} - 最適化されたタイトル
 */
export const getRegionalTitle = (location, type = 'room') => {
    if (!location) {
        return type === 'room'
            ? 'ルームシェア物件一覧 | 家賃相場・物件情報'
            : 'ルームメイト一覧 | 相性の良いルームシェア相手を探す';
    }

    // 地域種別（都道府県か市区町村か）に応じたタイトル
    const isPrefecture = !location.includes(' ') &&
        (location.endsWith('都') ||
            location.endsWith('府') ||
            location.endsWith('県') ||
            location.endsWith('道'));

    if (type === 'room') {
        return isPrefecture
            ? `${location}のルームシェア物件一覧 | 家賃相場・間取り・特徴`
            : `${location}の賃貸物件 | ルームシェア・シェアハウス情報`;
    } else {
        return isPrefecture
            ? `${location}でルームメイトを探す | 理想の同居人を見つける`
            : `${location}のルームシェア相手・ルームメイト募集情報`;
    }
};

/**
 * 地域ベースの検索ページ用説明文を生成
 * @param {string} location - 地域名
 * @param {string} type - 検索タイプ ('room'/'renter')
 * @returns {string} - 最適化された説明文
 */
export const getRegionalDescription = (location, type = 'room') => {
    if (!location) {
        return type === 'room'
            ? '日本全国のルームシェア物件を探せるマッチングサービス。家賃、間取り、立地条件など様々な条件で理想の住まいを見つけましょう。初めてのルームシェアでも安心のサポート体制。'
            : 'ルームシェア相手を探せるマッチングサービス。ライフスタイル、趣味、価値観が合う理想のルームメイトを見つけて、新しい生活をスタートしましょう。安心の身元確認システム完備。';
    }

    if (type === 'room') {
        return `${location}エリアのルームシェア物件・シェアハウスを探せます。家賃相場${getPriceRange(location)}、アクセス、設備など多彩な条件で検索可能。理想のルームメイトとの暮らしを始めるなら${location}の厳選物件から。安心・安全なルームシェア探しをサポートします。`;
    } else {
        return `${location}エリアでルームメイトを募集中のユーザーを探せます。ライフスタイル、予算、入居時期などの条件で理想の相性が良いルームシェア相手と出会えます。${location}で新生活をスタートするなら、価値観の合う同居人と暮らしましょう。`;
    }
};

/**
 * 物件詳細ページ用のタイトルを生成
 * @param {Object} room - 物件データ
 * @returns {string} - 最適化されたタイトル
 */
export const getRoomDetailTitle = (room) => {
    if (!room) return '物件詳細';

    // 特徴を抽出
    const features = [];
    if (room.roomDetails?.size) features.push(`${room.roomDetails.size}㎡`);
    if (room.roomType) features.push(room.roomType);
    if (room.roomDetails?.layout) features.push(room.roomDetails.layout);

    const featureText = features.length > 0 ? `${features.join('・')} | ` : '';
    const locationText = `${room.location.prefecture} ${room.location.city}`;
    const stationText = room.location.station ? `${room.location.station}駅徒歩${room.location.walkingMinutes}分 | ` : '';

    return `${locationText}の${room.propertyType || 'ルームシェア物件'} | ${stationText}${featureText}家賃${room.rentamount.toLocaleString()}円`;
};

/**
 * 物件詳細ページ用の説明文を生成
 * @param {Object} room - 物件データ
 * @returns {string} - 最適化された説明文
 */
export const getRoomDetailDescription = (room) => {
    if (!room) return '物件詳細ページです。';

    const locationText = `${room.location.prefecture} ${room.location.city}`;
    const typeText = `${room.propertyType || 'ルームシェア物件'} ${room.roomType || ''}`;
    const stationText = room.location.station ? `${room.location.line}線${room.location.station}駅から徒歩${room.location.walkingMinutes}分。` : '';
    const rentText = `家賃${room.rentamount.toLocaleString()}円/月。`;

    let amenities = '';
    if (room.roomDetails?.amenities && room.roomDetails.amenities.length > 0) {
        amenities = `主な設備：${room.roomDetails.amenities.slice(0, 3).join('、')}。`;
    }

    const descriptionIntro = `${locationText}の${typeText}。${stationText}${rentText}${amenities}`;

    // 物件説明があれば追加（最大100文字）
    let description = descriptionIntro;
    if (room.description) {
        description += ` ${room.description.substring(0, 100)}${room.description.length > 100 ? '...' : ''}`;
    }

    return description;
};

/**
 * ユーザー詳細ページ用のタイトルを生成
 * @param {Object} user - ユーザーデータ
 * @returns {string} - 最適化されたタイトル
 */
export const getUserDetailTitle = (user) => {
    if (!user || !user.profile) return 'ユーザープロフィール';

    // 特徴を抽出
    const features = [];
    if (user.profile.age) features.push(`${user.profile.age}歳`);
    if (user.profile.gender === 'male') features.push('男性');
    else if (user.profile.gender === 'female') features.push('女性');
    if (user.profile.occupation) features.push(user.profile.occupation);

    const featureText = features.length > 0 ? `${features.join('・')}` : '';

    // 希望エリアを取得
    const preferredArea = user.profile.preferences?.preferredAreas?.[0];
    const areaText = preferredArea ?
        `${preferredArea.prefecture}${preferredArea.city ? ` ${preferredArea.city}` : ''}のルームメイト | ` : '';

    return `${areaText}${featureText} | ${user.profile.nickname || 'ユーザー'}のプロフィール`;
};

/**
 * ユーザー詳細ページ用の説明文を生成
 * @param {Object} user - ユーザーデータ
 * @returns {string} - 最適化された説明文
 */
export const getUserDetailDescription = (user) => {
    if (!user || !user.profile) return 'ユーザープロフィールページです。';

    const nickname = user.profile.nickname || 'ユーザー';
    const genderText = user.profile.gender === 'male' ? '男性' :
        user.profile.gender === 'female' ? '女性' : '';
    const ageText = user.profile.age ? `${user.profile.age}歳` : '';

    // 希望エリア情報を取得
    const preferredArea = user.profile.preferences?.preferredAreas?.[0];
    const areaText = preferredArea ?
        `${preferredArea.prefecture}${preferredArea.city ? ` ${preferredArea.city}` : ''}` : '';
    const stationText = preferredArea?.station ?
        `${preferredArea.line}線${preferredArea.station}駅周辺` : '';

    let description = `${nickname}さんのプロフィール。`;

    // 基本情報を追加
    const basicInfo = [ageText, genderText].filter(Boolean).join('、');
    if (basicInfo) description += `${basicInfo}。`;

    // 職業情報
    if (user.profile.occupation) description += `職業: ${user.profile.occupation}。`;

    // 希望エリア情報を追加
    if (areaText) {
        description += `希望エリア: ${areaText}`;
        if (stationText) description += `（${stationText}）`;
        description += `。`;
    }

    // 希望家賃を追加
    if (user.profile.preferences?.rentRange) {
        const { min, max } = user.profile.preferences.rentRange;
        if (min && max) {
            description += `希望家賃: ${min.toLocaleString()}円〜${max.toLocaleString()}円。`;
        }
    }

    // 自己紹介があれば追加（最大100文字）
    if (user.profile.introduction) {
        description += ` ${user.profile.introduction.substring(0, 100)}${user.profile.introduction.length > 100 ? '...' : ''}`;
    }

    return description;
};

/**
 * 地域に応じた家賃相場目安を返す（SEO用）
 * @param {string} location - 地域名
 * @returns {string} - 家賃相場の範囲
 */
const getPriceRange = (location) => {
    // 主要都市の家賃相場目安（SEO用）
    const priceRanges = {
        '東京都': '7万〜15万円',
        '渋谷区': '9万〜18万円',
        '新宿区': '8万〜17万円',
        '世田谷区': '7万〜14万円',
        '大阪府': '5万〜10万円',
        '大阪市': '6万〜12万円',
        '京都府': '5万〜9万円',
        '京都市': '6万〜10万円',
        '福岡県': '4万〜8万円',
        '福岡市': '5万〜9万円',
        '名古屋市': '5万〜10万円'
    };

    return priceRanges[location] || '4万〜10万円';
}; 